/* ==========================================================================
   FLC Forge — Quick Add
   ========================================================================== */

class QuickAddButton extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-quick-add-btn]');
    this.variantId = this.dataset.variantId;
    this.productUrl = this.dataset.productUrl;
    this.hasVariants = this.dataset.hasVariants === 'true';

    this.btn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.hasVariants ? this.openModal() : this.addToCart();
    });
  }

  async addToCart() {
    if (!this.variantId) return;
    this.setLoading(true);

    try {
      const data = await FLC.fetchJSON(window.flcForge.routes.cart_add_url, {
        method: 'POST',
        body: JSON.stringify({ id: this.variantId, quantity: 1 })
      });
      FLC.dispatch('flc:cart:refresh');
      FLC.dispatch('flc:cart:open');
      window.liveRegion?.announce('Added to cart');
    } catch (err) {
      console.error('Quick add failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  openModal() {
    const modal = document.getElementById('quick-add-modal');
    if (modal) {
      modal.open(this.productUrl);
    } else {
      window.location.href = this.productUrl;
    }
  }

  setLoading(state) {
    if (this.btn) {
      this.btn.disabled = state;
      this.btn.setAttribute('aria-busy', state);
    }
  }
}

customElements.define('quick-add-button', QuickAddButton);

/* Quick Add Variant — single-variant pill on product card
   ========================================================================== */

class QuickAddVariant extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('button');
    this.variantId = this.dataset.variantId;
    this.productUrl = this.dataset.productUrl;

    this.btn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart();
    });
  }

  async addToCart() {
    if (!this.variantId) return;
    this.setLoading(true);

    try {
      await FLC.fetchJSON(window.flcForge.routes.cart_add_url, {
        method: 'POST',
        body: JSON.stringify({ id: Number(this.variantId), quantity: 1 })
      });
      FLC.dispatch('flc:cart:refresh');
      FLC.dispatch('flc:cart:open');
      this.setAdded();
    } catch (err) {
      console.error('Quick add variant failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(state) {
    if (this.btn) {
      this.btn.disabled = state;
      this.btn.setAttribute('aria-busy', String(state));
    }
  }

  setAdded() {
    this.btn?.classList.add('is-added');
    setTimeout(() => this.btn?.classList.remove('is-added'), 1500);
  }
}

customElements.define('quick-add-variant', QuickAddVariant);

/* Quick Add Modal
   ========================================================================== */
class QuickAddModal extends HTMLElement {
  connectedCallback() {
    this.inner   = this.querySelector('[data-modal-inner]');
    this.closeBtn = this.querySelector('[data-modal-close]');

    this.closeBtn?.addEventListener('click', () => this.close());
    this.addEventListener('click', (e) => {
      // Colour swatch (product-siblings) — swap the modal to that product
      // instead of navigating away from the cart.
      const swatch = e.target.closest('[data-sibling]');
      if (swatch) {
        e.preventDefault();
        this.open(swatch.getAttribute('href'));
        return;
      }
      if (e.target === this) this.close();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    document.addEventListener('flc:drawers:close-all', () => this.close());

    // Close once the add-to-cart inside this modal succeeds (ProductForm
    // dispatches this on every successful add, PDP and here alike — safe to
    // key off it since the overlay + focus trap mean nothing else can submit
    // an add-to-cart while this modal is open).
    document.addEventListener('flc:cart:refresh', () => {
      if (this.getAttribute('aria-hidden') === 'false') this.close();
    });
  }

  async open(productUrl) {
    const isReopen = this.getAttribute('aria-hidden') === 'false';

    if (this.inner) {
      // Colour-swap reload: lock the current height first so the modal doesn't
      // collapse to a single spinner line then pop back open — it just shows
      // the spinner over the existing size instead.
      if (isReopen) this.inner.style.minHeight = `${this.inner.offsetHeight}px`;
      this.inner.innerHTML = '<div class="quick-add-modal__loading"><span class="loading-spinner" aria-hidden="true"></span></div>';
    }

    this.setAttribute('aria-hidden', 'false');
    FLC.openOverlay('quick-add-modal');
    FLC.trapFocus(this);

    if (!this.inner) return;

    try {
      // Recommended-product URLs already carry their own tracking query string
      // (?pr_prod_strat=...&pr_rec_id=...), so a bare `?` here would produce an
      // invalid double-`?` URL that Shopify silently ignores, serving the normal
      // page instead of the section JSON.
      const separator = productUrl.includes('?') ? '&' : '?';
      const url = `${productUrl}${separator}sections=quick-add-form`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json  = await res.json();
      const html  = json['quick-add-form'];
      const doc   = html && new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc && doc.getElementById('quick-add-form');
      if (!fresh) throw new Error('No quick-add-form section in response');

      // outerHTML (not innerHTML) — the .quick-add-form class on `fresh`
      // itself carries the layout CSS, so it must be preserved on injection.
      // Assigning innerHTML on an already-connected element auto-upgrades and
      // fires connectedCallback() natively on the injected custom elements —
      // calling it again here would double-register their listeners.
      this.inner.innerHTML = fresh.outerHTML;
    } catch (err) {
      console.error('Quick add modal load failed:', err);
      this.inner.innerHTML = '<p class="quick-add-modal__status">Sorry, this item couldn\'t be loaded.</p>';
    } finally {
      this.inner.style.minHeight = '';
    }
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    FLC.closeOverlay('quick-add-modal');
    if (this.inner) {
      this.inner.innerHTML = '';
      this.inner.style.minHeight = '';
    }
  }
}

customElements.define('quick-add-modal', QuickAddModal);
